import { LockOpen } from 'lucide-preact'
import type { AssetQaFindingDto } from '@/types/assetQa'
import { QA_SEVERITY_META } from '../constants/assetQaMeta'
import { shortAgo } from '@/utils/time'
import { groupFindings, describeGroup, sampleHexes, type AssetQaGroup } from './assetQaGrouping'

interface Props {
    findings: AssetQaFindingDto[]
    canOverride: boolean
    onOverride: (group: AssetQaGroup<AssetQaFindingDto>) => void
    overridingKey: string | null
}

/**
 * The fix list, in the shape the rulebook's report format locks down: three lines per finding —
 * code and title, what was found and where, and the fix.
 *
 * The succinctness is the feature, not a style preference. A reader should be able to find any
 * issue and know how to fix it in seconds, which is why there is no commentary section here and
 * nothing is truncated behind a "show more".
 *
 * ⚑ Findings that are one finding said many times are folded (ORB-967). A gradient is sampled as
 * dozens of flat regions, so it raises dozens of findings that all name one palette role and all
 * print the same sentence. Forty-three of those defeat the succinctness rule above rather than
 * serving it — and they asked for forty-three written reasons to dismiss one decision. The members
 * are still sent, still overridden individually, and still one expand away.
 */
export function AssetQaFixList({ findings, canOverride, onOverride, overridingKey }: Props) {
    if (findings.length === 0) return null

    return (
        <ul className="space-y-3">
            {groupFindings(findings).map(group => (
                <FixListRow
                    key={group.key}
                    group={group}
                    canOverride={canOverride}
                    onOverride={onOverride}
                    overridingKey={overridingKey}
                />
            ))}
        </ul>
    )
}

function FixListRow({
    group,
    canOverride,
    onOverride,
    overridingKey,
}: {
    group: AssetQaGroup<AssetQaFindingDto>
    canOverride: boolean
    onOverride: (group: AssetQaGroup<AssetQaFindingDto>) => void
    overridingKey: string | null
}) {
    const finding = group.lead
    const severity = QA_SEVERITY_META[finding.Severity] ?? QA_SEVERITY_META.Minor

    const summary = describeGroup(group)
    const samples = sampleHexes(group)

    // ⚑ Only an Open finding can be overridden. The gate used to be "not Overridden", which offered
    // the action on Fixed and False Positive too — both terminal, and both already resolved as far
    // as this panel is concerned. Accepting it would replace the record of why a finding was closed
    // with "somebody dismissed it", losing exactly the distinction the override reason exists to
    // preserve. Raised by Copilot on #137.
    const open = group.members.filter(member => member.Status === 'Open')
    const overridden = group.members.filter(member => member.Status === 'Overridden')
    const allOverridden = overridden.length === group.members.length

    return (
        <li
            className={`rounded-md border p-3 ${
                allOverridden
                    ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
            }`}
        >
            {/* Line 1 — code and title. */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ${severity.classes}`}>
                    {finding.Severity}
                </span>
                <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {finding.Code}
                </span>
                <span className={`text-sm font-medium ${
                    allOverridden
                        ? 'text-slate-500 dark:text-slate-400 line-through'
                        : 'text-slate-900 dark:text-slate-100'
                }`}>
                    {finding.Title}
                </span>
                {summary && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        ×{group.members.length}
                    </span>
                )}
            </div>

            {/* Line 2 — the evidence: what was found, where, and the measurement behind it. ONE
                element, not three.

                ⚑⚑ §6 of the rulebook gives every finding exactly three lines — header, evidence,
                fix — and its own example carries the measurement on the evidence line beside Found
                and Where:

                  Found: #2E5F8A sampled, ΔE 14.2 from Laser Green #52DF45 · Where: p2 CTA

                These had drifted to three separate paragraphs while the comments still said
                "Line 2", which is how a format rule stops being followed without anyone deciding to
                stop following it.

                The FIELDS stay separate in the data, and deliberately so: Found is the judgement,
                Measured is what a challenge re-measures against, and Webster reads them apart. Only
                the render joins them. Measured is muted and monospaced within the line so whoever is
                triaging can skip past it.

                ⚑ For a folded group the members disagree on Found — a different shade each — so the
                lead's sentence would be quietly wrong for the other 42. The summary says what they
                share and the expansion below carries the rest. */}
            <p className="mt-1.5 text-xs text-slate-600 dark:text-slate-300 break-words">
                {summary ?? finding.Found}
                {summary && samples.length > 0 && (
                    <span className="font-mono text-slate-400 dark:text-slate-500">
                        {' '}· e.g. {samples.join(', ')}
                    </span>
                )}
                {/* ⚑ Where belongs to ONE shade, so it is dropped from a folded summary. A 43-shade
                    card reading "Where: 1% of the asset" states the lead's own coverage as though
                    it described all of them — the other 42 each have their own, and the sum is not
                    1%. Each member carries its own in the expansion below.
                    Raised by Copilot on PR #230. */}
                {!summary && finding.Where && (
                    <span className="text-slate-500 dark:text-slate-400"> · {finding.Where}</span>
                )}
                {!summary && finding.Measured && (
                    <span className="font-mono text-slate-400 dark:text-slate-500"> · {finding.Measured}</span>
                )}
            </p>

            {/* Line 3 — the fix, as an instruction. */}
            <p className="mt-1.5 text-xs font-medium text-slate-800 dark:text-slate-200 break-words">
                {finding.Fix}
            </p>

            {summary && (
                <details className="mt-1.5">
                    <summary className="text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded">
                        Show all {group.members.length}
                    </summary>
                    <ul className="mt-1 ml-1 space-y-0.5 border-l border-slate-200 dark:border-slate-700 pl-2">
                        {group.members.map(member => (
                            <li
                                key={member.CheckId}
                                className="text-[11px] text-slate-500 dark:text-slate-400 break-words"
                            >
                                {member.Found}
                                {member.Where && (
                                    <span className="text-slate-400 dark:text-slate-500"> · {member.Where}</span>
                                )}
                                {member.Measured && (
                                    <span className="font-mono text-slate-400 dark:text-slate-500"> · {member.Measured}</span>
                                )}
                                {member.Status === 'Overridden' && member.Override && (
                                    <span className="text-slate-400 dark:text-slate-500">
                                        {' '}· overridden by {member.Override.ByName}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            {/* An override never removes the finding above it. It is shown as what it is: something
                raised, and a named person's decision that it should not stand. A reader can judge
                both. */}
            {overridden.length > 0 && overridden[0].Override && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200 dark:border-slate-700">
                    <p className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                        {allOverridden
                            ? `Overridden by ${overridden[0].Override.ByName}`
                            : `${overridden.length} of ${group.members.length} overridden by ${overridden[0].Override.ByName}`}
                        <span className="font-normal text-slate-400 dark:text-slate-500">
                            {' '}· {shortAgo(overridden[0].Override.AtUtc)}
                        </span>
                    </p>
                    <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300 break-words">
                        {overridden[0].Override.Reason}
                    </p>
                </div>
            )}

            {canOverride && open.length > 0 && (
                <button
                    type="button"
                    onClick={() => onOverride(group)}
                    disabled={overridingKey !== null}
                    className="mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                    <LockOpen className="w-3 h-3" />
                    {overridingKey === group.key
                        ? 'Saving…'
                        : open.length > 1 ? `Override all ${open.length}` : 'Override'}
                </button>
            )}
        </li>
    )
}
